import { CheckIcon } from "@heroicons/react/outline";
import React, { useState } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  addToCart,
  requestListPackage,
  requestListUserSubscription,
  useAuthState,
  useCheckoutDispatch,
  useSubscriptionDispatch,
  useSubscriptionState,
} from "../../Context";
import { classNames, convertToGoogleAnalyticsItem, trackAddToCart } from "../../Utils";
import { Alert } from "../../Components/Alert";
import { Button } from "../../Components/Button";
import { analytics } from "../../firebase";
import { logEvent } from "firebase/analytics";
import { EmptyList } from "../../Components/EmptyList";

const Subscription = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useSubscriptionDispatch();
  const { packages, userSubscriptions, loading, error } =
    useSubscriptionState();

  const checkoutDispatch = useCheckoutDispatch();
  const { user } = useAuthState();

  const [availablePackages, setavailablePackages] = useState([]);

  useEffect(() => {
    if (user.id) {
      requestListPackage(dispatch);
      requestListUserSubscription(dispatch, { userID: user.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  useEffect(() => {
    const allPackages = packages.map((item) => {
      userSubscriptions.forEach((sub) => {
        if (sub.packageID === item.id) {
          item.subscribed = true;
          item.expiredAt = sub.expiredAt;
        }
      });
      return item;
    });

    const analyticsParams = {
      item_list_id: "subscription",
      item_list_name: "Available Subscriptions",
      items: allPackages.map((product) =>
        convertToGoogleAnalyticsItem(product)
      ),
    };
    logEvent(analytics, "view_item_list", analyticsParams);

    setavailablePackages(allPackages);
  }, [packages, userSubscriptions]);

  const onSubscribe = (plan) => {
    let products = [];
    products.push({
      id: plan.id,
      type: "subscription",
      name: t(plan.title),
      detail: {
        description: t(plan.description),
      },
      price: plan.price,
      quantity: 1,
    });

    const analyticsParams = {
      currency: "IDR",
      value: plan.price,
      items: products.map((product) => convertToGoogleAnalyticsItem(product)),
    };
    logEvent(analytics, "add_to_cart", analyticsParams);
    trackAddToCart({ products });

    addToCart(checkoutDispatch, {
      products,
    });

    navigate("/checkout");
  };

  return (
    <div className="max-w-3xl lg:max-w-7xl">
      {/* Header with navigation */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {userSubscriptions.length > 0 && (
            <Button
              onClick={() => navigate('/my-subscriptions')}
              title="mySubscriptions"
            />
          )}
        </div>
      </div>

      {error && (
        <div className="bg-white min-h-full rounded-md sm:rounded-lg shadow-sm p-4">
          <Alert
            type="error"
            title="Error"
            message={error?.message || "Error fetching subscriptions."}
            errors={error?.errors}
          />
        </div>
      )}
      <div className="space-y-8 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-x-8 lg:gap-y-8">
        {loading ? (
          <div className="relative p-8 bg-white border border-gray-200 rounded-2xl shadow-sm flex flex-col animate-pulse">
            <div className="flex-1">
              <div className="h-4 w-[50%] mt-2 mb-1 bg-gray-300 rounded-md"></div>
              <div className="mt-4 flex items-baseline">
                <div className="h-6 w-[50%] mt-2 mb-1 mr-2 bg-gray-300 rounded-md"></div>
                <div className="h-6 w-[25%] mt-2 mb-1 bg-gray-300 rounded-md"></div>
              </div>
              <div className="h-3 w-full mt-8 mb-1 bg-gray-300 rounded-md"></div>
              <div className="h-3 w-[50%] mt-2 mb-1 bg-gray-300 rounded-md"></div>
              <div className="mt-10">
                <div className="h-3 w-full mt-4 mb-1 bg-gray-300 rounded-md"></div>
                <div className="h-3 w-full mt-4 mb-1 bg-gray-300 rounded-md"></div>
                <div className="h-3 w-full mt-4 mb-1 bg-gray-300 rounded-md"></div>
                <div className="h-3 w-full mt-4 mb-1 bg-gray-300 rounded-md"></div>
              </div>
            </div>
            <div className="mt-10">
              <div className="h-12 w-full mt-2 bg-gray-300 rounded-md"></div>
            </div>
          </div>
        ) : availablePackages.length > 0 ? (
          availablePackages.map((tier) => (
            <div
              key={tier.id}
              className="relative p-8 bg-white border border-gray-200 rounded-2xl shadow-sm flex flex-col"
            >
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900">
                  {tier.title}
                </h3>
                {tier.mostPopular ? (
                  <p className="absolute top-0 py-1.5 px-4 bg-blue-500 rounded-full text-xs font-semibold uppercase tracking-wide text-white transform -translate-y-1/2">
                    {t("Most Popular")}
                  </p>
                ) : null}
                <p className="mt-4 flex items-baseline text-gray-900">
                  <span className="text-3xl font-extrabold tracking-tight">
                    <NumericFormat
                      displayType={"text"}
                      value={tier.price}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp"}
                    />
                  </span>
                  <span className="ml-1 text-xl font-semibold">
                    {tier.periodType === "MONTHLY" && `/${t("month")}`}
                    {tier.periodType === "YEARLY" && `/${t("year")}`}
                  </span>
                </p>
                <p className="mt-6 text-gray-500">{tier.description}</p>

                {/* Feature list */}
                <ul className="mt-6 space-y-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex">
                      <CheckIcon
                        className="flex-shrink-0 w-6 h-6 text-blue-500"
                        aria-hidden="true"
                      />
                      <span className="ml-3 text-gray-500">{t(feature)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {tier.subscribed || user.membership !== "FREE" ? (
                <button
                  disabled
                  className={classNames(
                    tier.subscribed ? "text-emerald-500" : "text-slate-500",
                    "bg-blue-50 mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium"
                  )}
                >
                  {tier.subscribed ? (
                    <span>
                      {t("Active until")}{" "}
                      {dayjs(tier.expiredAt)
                        .locale(i18n.language)
                        .format("DD MMM YYYY HH:mm")}
                    </span>
                  ) : (
                    <span>{t("Already a member")}</span>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => onSubscribe(tier)}
                  className={classNames(
                    tier.mostPopular
                      ? "bg-blue-500 text-white hover:bg-blue-600"
                      : "bg-blue-50 text-blue-700 hover:bg-blue-100",
                    "mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium"
                  )}
                >
                  <span>{t("Subscribe")}</span>
                </button>
              )}
            </div>
          ))
        ) : (
          <div className="bg-white col-span-12 rounded-lg p-4">
            <EmptyList title="noPackagesAvailable" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscription;
